import axiosHttpClient from '#helpers/axiosHttpClient.js'
import handleApiError from '#helpers/logging/handleApiError.js'
import Logger from '#helpers/logging/Logger.js'
import { url_auth_permissions } from '#modules/auth/constants/endpoints.js'
import { defaultExpiresTime, errorRetryTime, nowTime } from '#stores/storeDefaults.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

export const usePermissionsStore = defineStore('permissions', () => {
	const permissions = useStorage('permissions', [], sessionStorage)
	const loading = shallowRef(false)
	const expires = useStorage('permissions-expires', null, sessionStorage)

	/**
	 * Check if the current user has ALL of these permissions.
	 * @param {string|string[]} permissionNames - The list of permissions to check. Can also be a single permission
	 * @returns {boolean} - Whether the user has all the permissions given
	 */
	function has(...args) {
		if (args.length === 0) return true
		if (args.length === 1) {
			if (typeof args[0] === 'string') {
				return permissions.value.includes(args[0])
			}
			if (Array.isArray(args[0])) {
				const checkPermissions = args[0].map(permissionName =>
					permissions.value?.includes(permissionName),
				)
				return checkPermissions.every(permission => permission === true)
			}
		}

		if (args.length > 1) {
			const checkPermissions = args.map(permissionName =>
				permissions.value?.includes(permissionName),
			)
			return checkPermissions.every(permission => permission === true)
		}

		throw new Error('permissionNames needs to be an Array of Strings, or a single String')
	}

	/**
	 * Check if the current user has ANY of these permissions.
	 * @param {string|string[]} permissionNames - The list of permissions to check. Can also be a single permission
	 * @returns {boolean} - Whether the user has any of the permissions given
	 */
	function hasAny(...permissionNames) {
		// fetchPermissions()
		const checkPermissions = permissionNames.map(permissionName =>
			permissions.value.includes(permissionName),
		)
		return checkPermissions.includes(true)
	}

	const list = computed(() => {
		// fetchPermissions()
		return permissions.value.map(permission => permission)
	})

	/**
	 * Fetch all the permissions.
	 * @param {boolean} [force] - Whether to force fetching the permissions
	 * @returns {Promise} - A promise to act upon
	 */
	async function fetchPermissions(force = false) {
		if (force) {
			loading.value = false
		}
		if (loading.value) {
			return
		}
		if (new Date().getTime() < expires.value && !force) {
			return
		}
		const auth = useAuthorizationStore()
		if (!auth.accessToken) {
			return
		}
		Logger.debug('usePermissionsStore.fetchPermissions')

		loading.value = true
		await axiosHttpClient
			.get(url_auth_permissions)
			.then(response => {
				const data = response?.data?.data ?? []
				permissions.value = data.map(permission => permission?.name)
				expires.value = nowTime() + defaultExpiresTime
			})
			.catch(e => {
				expires.value = nowTime() + errorRetryTime
				handleApiError(e)
			})
			.finally(() => {
				loading.value = false
			})
	}

	/**
	 * Clean up the data.
	 */
	function remove() {
		loading.value = false
		permissions.value = []
		expires.value = 0
	}

	return { permissions, loading, expires, has, hasAny, list, fetch: fetchPermissions, remove }
})
